/**
 * Query-key factory for the React Query layer (Plan 3).
 * Single source of key shapes: build keys ONLY through this object so
 * invalidation can't typo. Hierarchy rule: every leaf extends its domain
 * root, so `invalidateQueries({ queryKey: queryKeys.x.all })` catches all
 * of domain x.
 */
export const queryKeys = {
  chatSettings: {
    all: ['chat-settings'] as const,
    global: () => [...queryKeys.chatSettings.all, 'global'] as const,
    runtimeStatus: () => [...queryKeys.chatSettings.all, 'runtime-status'] as const,
    knowledgeIndexable: () => [...queryKeys.chatSettings.all, 'knowledge-indexable'] as const,
  },
  adminCourses: {
    all: ['admin-courses'] as const,
    list: () => [...queryKeys.adminCourses.all, 'list'] as const,
    availableUsers: () => [...queryKeys.adminCourses.all, 'available-users'] as const,
  },
  roster: {
    all: ['course-roster'] as const,
    workspace: (courseId: string, page = 1, search = '', filtersKey = '') =>
      [...queryKeys.roster.all, 'workspace', courseId, page, search, filtersKey] as const,
    evidenceSummary: (courseId: string, userKey: string) =>
      [...queryKeys.roster.all, 'evidence-summary', courseId, userKey] as const,
    evidenceTab: (courseId: string, userKey: string, tab: string, page = 1, search = '') =>
      [...queryKeys.roster.all, 'evidence-tab', courseId, userKey, tab, page, search] as const,
    assessmentDetail: (courseId: string, userKey: string, submissionId: string) =>
      [...queryKeys.roster.all, 'assessment-detail', courseId, userKey, submissionId] as const,
    promptDetail: (courseId: string, userKey: string, responseId: string) =>
      [...queryKeys.roster.all, 'prompt-detail', courseId, userKey, responseId] as const,
    progressDetail: (courseId: string, userKey: string, instanceId: string) =>
      [...queryKeys.roster.all, 'progress-detail', courseId, userKey, instanceId] as const,
    evidence: (courseId: string, userKey: string) =>
      [...queryKeys.roster.all, 'evidence', courseId, userKey] as const,
    credentialOptions: (courseId: string, userKey: string) =>
      [...queryKeys.roster.all, 'credential-options', courseId, userKey] as const,
  },
  campaigns: {
    all: ['campaigns'] as const,
    manifest: (campaignId: string) => [...queryKeys.campaigns.all, 'manifest', campaignId] as const,
    courseManifests: (courseId: string) => [...queryKeys.campaigns.all, 'course-manifests', courseId] as const,
  },
  companies: {
    all: ['companies'] as const,
    list: () => [...queryKeys.companies.all, 'list'] as const,
    groups: () => [...queryKeys.companies.all, 'groups'] as const,
    bypassCodes: (companyId: string) => [...queryKeys.companies.all, 'bypass-codes', companyId] as const,
  },
  roadmapInstance: {
    all: ['roadmap-instance'] as const,
    detail: (instanceId: string, viewAsStudentId: string | null) =>
      [...queryKeys.roadmapInstance.all, 'detail', instanceId, viewAsStudentId] as const,
    stepAiConfig: (stepIdsKey: string) =>
      [...queryKeys.roadmapInstance.all, 'step-ai-config', stepIdsKey] as const,
  },
  student: {
    all: ['student'] as const,
    enrollments: (userId: string) => [...queryKeys.student.all, 'enrollments', userId] as const,
    courseCatalog: () => [...queryKeys.student.all, 'course-catalog'] as const,
    brands: (userId: string) => [...queryKeys.student.all, 'brands', userId] as const,
    dashboard: (userId: string) => [...queryKeys.student.all, 'dashboard', userId] as const,
    // Omitting userId intentionally retains the course-wide invalidation prefix.
    courseDetail: (courseId: string, userId?: string) => userId
      ? [...queryKeys.student.all, 'course-detail', courseId, userId] as const
      : [...queryKeys.student.all, 'course-detail', courseId] as const,
    instructorCheck: (userId: string, courseId: string) =>
      [...queryKeys.student.all, 'instructor-check', userId, courseId] as const,
    paymentStatus: (userId: string, courseId: string) =>
      [...queryKeys.student.all, 'payment-status', userId, courseId] as const,
    lessonsPage: (userId: string, isAdmin: boolean) =>
      [...queryKeys.student.all, 'lessons-page', userId, isAdmin] as const,
    lessonInstance: (instanceId: string) => [...queryKeys.student.all, 'lesson-instance', instanceId] as const,
  },
  kb: {
    all: ['kb'] as const,
    categories: () => [...queryKeys.kb.all, 'categories'] as const,
    homeSettings: () => [...queryKeys.kb.all, 'home-settings'] as const,
    startCards: () => [...queryKeys.kb.all, 'start-cards'] as const,
    publishedPosts: () => [...queryKeys.kb.all, 'published-posts'] as const,
    allPosts: () => [...queryKeys.kb.all, 'all-posts'] as const,
    tree: () => [...queryKeys.kb.all, 'tree'] as const,
    taxonomyNodes: () => [...queryKeys.kb.all, 'taxonomy-nodes'] as const,
    canonicalPosts: () => [...queryKeys.kb.all, 'canonical-posts'] as const,
    placements: () => [...queryKeys.kb.all, 'placements'] as const,
    articleContext: (postId: string) => [...queryKeys.kb.all, 'article-context', postId] as const,
    search: (query: string) => [...queryKeys.kb.all, 'search', query] as const,
    health: () => [...queryKeys.kb.all, 'content-health'] as const,
  },
};
